import { useState } from 'react';
import { message } from 'antd';
import { useMutation } from '@apollo/client';
import { showError } from '../../../utils/exceptionUtils';
import { OrderCloseFiltersInput } from '../graphql/queries/getPendingCloseOrders';
import {
    GENERATE_ORDER_CLOSE_DOWNLOAD_REPORT,
    GenerateOrderCloseDownloadReportResponse,
    GenerateOrderCloseDownloadReportVariables,
} from '../graphql/mutations/generateOrderCloseDownloadReport';

export interface UseOrderCloseDownloadResult {
    downloadLoading: boolean;
    downloadOrders: (filters: OrderCloseFiltersInput) => Promise<void>;
}

export interface UseOrderCloseDownloadOptions {
    /** 任务已成功提交到后端时回调（用于弹出与包裹下载一致的说明弹窗） */
    onSubmitted?: (fileName: string) => void;
}

export function useOrderCloseDownload(options?: UseOrderCloseDownloadOptions): UseOrderCloseDownloadResult {
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [generateReport] = useMutation<
        GenerateOrderCloseDownloadReportResponse,
        GenerateOrderCloseDownloadReportVariables
    >(GENERATE_ORDER_CLOSE_DOWNLOAD_REPORT);

    const convertTimeRange = (
        range: [string | null, string | null] | null | undefined
    ): { start: string; end: string } | undefined => {
        if (!range || range.length !== 2 || !range[0] || !range[1]) {
            return undefined;
        }
        return { start: range[0], end: range[1] };
    };

    const downloadOrdersHandler = async (filters: OrderCloseFiltersInput) => {
        try {
            setDownloadLoading(true);

            const backendSupportedFilters: Record<string, unknown> = {
                trackingNumbers: filters.trackingNumbers,
                referenceNos: filters.referenceNos,
                channelOrderNumber: filters.channelOrderNumber,
                clientCodes: filters.clientCodes,
                channelIds: filters.channelIds,
                deliveredDspIds: filters.deliveredDspIds,
                daysFromAscan: filters.daysFromAscan,
                hoursToClose: filters.hoursToClose,
                hasException: filters.hasException,
                exceptionStatuses: filters.exceptionStatuses || undefined,
                hasCloseRecord: filters.hasCloseRecord,
                flowType: filters.flowType,
                overallStatuses: filters.overallStatuses,
                sortingCodeSegment1: filters.sortingCodeSegment1,
                sortingCodeSegment2: filters.sortingCodeSegment2,
                sortingCodeSegment3: filters.sortingCodeSegment3,
                orderTime: convertTimeRange(filters.orderTime),
                receivedAtHub: convertTimeRange(filters.receivedAtHub),
                deliveredAt: convertTimeRange(filters.deliveredAt),
                destHubArrivedAt: convertTimeRange(filters.destHubArrivedAt),
                destWarehouseArrivedAt: convertTimeRange(filters.destWarehouseArrivedAt),
                lastDeliveryScanTime: convertTimeRange(filters.lastDeliveryScanTime),
                deliverAt: convertTimeRange(filters.deliverAt),
                updatedAt: convertTimeRange(filters.updatedAt),
                returnArrivalHubAt: convertTimeRange(filters.returnArrivalHubAt),
                exceptionReasonCode: filters.exceptionReasonCode,
                markExceptionAt: convertTimeRange(filters.markExceptionAt),
                nextAction: filters.nextAction,
                nextActions: filters.nextActions,
                isComplaint: filters.isComplaint,
                podReviewStatus: filters.podReviewStatus,
                recipientZipcode: filters.receiverZipcodeInput
                    ? filters.receiverZipcodeInput
                          .split(/[,\n\r\s]+/)
                          .map(s => s.trim())
                          .filter(s => s.length > 0)
                    : filters.receiverZipcode,
                pickupHubId:
                    filters.pickupHubId?.length === 1 ? parseInt(filters.pickupHubId[0], 10) : undefined,
                pickupHubIds:
                    filters.pickupHubId && filters.pickupHubId.length > 0
                        ? filters.pickupHubId.map(v => parseInt(v, 10))
                        : undefined,
                cooperationWarehouseId: filters.cooperationWarehouseId,
                closeTime: convertTimeRange(filters.closeTime),
                closeOperatorAccountId: filters.closeOperatorAccountId,
            };

            const cleanFilters: Record<string, unknown> = {};
            Object.keys(backendSupportedFilters).forEach(key => {
                const value = backendSupportedFilters[key];
                if (value !== undefined && value !== null && !(Array.isArray(value) && value.length === 0)) {
                    cleanFilters[key] = value;
                }
            });

            const queryFilters: OrderCloseFiltersInput =
                Object.keys(cleanFilters).length > 0 ? (cleanFilters as OrderCloseFiltersInput) : {};

            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            const response = await generateReport({
                variables: {
                    filters: queryFilters,
                    timezone: userTimezone,
                },
            });

            const fileName = response.data?.generateOrderCloseDownloadReport;
            if (fileName) {
                options?.onSubmitted?.(fileName);
            } else {
                message.error('提交导出任务失败');
            }
        } catch (error: unknown) {
            console.error('关单工具导出失败:', error);
            showError(error);
        } finally {
            setDownloadLoading(false);
        }
    };

    return {
        downloadLoading,
        downloadOrders: downloadOrdersHandler,
    };
}
